import { KpiTile } from "@/components/dashboard/kpi-tile";
import { metricIcon } from "@/components/dashboard/metric-meta";
import { FadeIn } from "@/components/ui/motion";
import { cn } from "@/lib/utils";
import type { MetricDef, Benchmark } from "@/lib/api/types";

interface MetricTilesProps {
  /** Metric keys to render, in order. */
  keys: string[];
  /** Current values keyed by metric key (Kpis + any extras like influencer_collabs). */
  values: Record<string, number | null | undefined>;
  /** Previous-period values for trend deltas. */
  prev?: Record<string, number | null | undefined>;
  metrics: MetricDef[];
  /** Optional benchmarks — a matching metric_key shows its target on the tile. */
  benchmarks?: Benchmark[];
  columns?: 3 | 4;
  /** Currency formatter for currency-unit tiles (from useMoney). */
  money?: (v: number | null | undefined) => string;
  /** Per-metric label overrides, e.g. to say which window a figure really covers. */
  labels?: Record<string, string>;
  /** Per-metric caveat shown under the value (partial coverage). */
  notes?: Record<string, string | undefined>;
  /** Metrics whose value is still being fetched. */
  pending?: string[];
  className?: string;
}

/** A responsive grid of standalone `KpiTile` cards (gap, not hairlines). */
export function MetricTiles({ keys, values, prev, metrics, benchmarks, columns = 4, money, labels, notes, pending, className }: MetricTilesProps) {
  const grid = columns === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4";
  const validKeys = keys.filter((k) => metrics.some((m) => m.key === k));
  return (
    <FadeIn>
      <div className={cn("grid grid-cols-1 gap-3", grid, className)}>
        {validKeys.map((k) => {
          const def = metrics.find((m) => m.key === k)!;
          const target = benchmarks?.find((b) => b.metric_key === k && !b.region && !b.brand_id)?.target_value ?? null;
          return (
            <KpiTile
              key={k}
              label={labels?.[k] ?? def.short_label}
              value={values[k] ?? null}
              prev={prev?.[k] ?? null}
              unit={def.unit}
              icon={metricIcon(k)}
              target={target}
              higherIsBetter={def.higher_is_better}
              money={money}
              description={def.description}
              note={notes?.[k]}
              pending={pending?.includes(k)}
            />
          );
        })}
      </div>
    </FadeIn>
  );
}
